import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Search, Loader2, UserCheck, ClipboardPaste } from 'lucide-react';
import {
  useSearchProfiles,
  rankProfiles,
  type RankedProfileResult,
} from '@/hooks/useSearchProfiles';
import { resolveIdentifier } from '@/hooks/useCardData';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFollows } from '@/hooks/useFollows';
import { sanitizeImageUrl } from '@/lib/sanitizeUrl';
import { cn } from '@/lib/utils';

export function ProfileSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: rawResults = [], isFetching } = useSearchProfiles(query);
  const { user } = useCurrentUser();
  const { follows } = useFollows();

  // Prioritize the current user and their follows, like Ditto does.
  const results = useMemo(
    () => rankProfiles(rawResults, user?.pubkey, follows),
    [rawResults, user?.pubkey, follows],
  );

  // Detect if the raw input is itself a valid identifier (npub/nprofile/hex).
  const directId = resolveIdentifier(query);

  // Reset the highlighted row whenever the result set changes. Adjusting state
  // during render (with a stored previous value) is React's recommended pattern
  // over an effect that only mirrors derived state.
  //
  // The comparison key is the list of pubkeys, not the array reference: an idle
  // query re-defaults `rawResults` to a fresh `[]` every render, so `results`
  // is a new reference each time even when nothing changed. Keying on content
  // keeps this from looping (a stable string ends the render-time update).
  const resultsKey = results.map((r) => r.pubkey).join(',');
  const [prevKey, setPrevKey] = useState(resultsKey);
  if (resultsKey !== prevKey) {
    setPrevKey(resultsKey);
    setHighlight(0);
  }

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const go = (pubkey: string) => {
    navigate(`/${nip19.npubEncode(pubkey)}`);
  };

  // Pasting a bare identifier is a "go there now" gesture, so jump straight to
  // the card rather than making the user press Enter. Returns whether it did.
  const goToIdentifier = (text: string) => {
    const id = resolveIdentifier(text.trim());
    if (id) {
      navigate(`/${id.npub}`);
      return true;
    }
    return false;
  };

  const handlePaste = async () => {
    // `navigator.clipboard.readText()` is unavailable to web pages in Firefox
    // and blocked outside secure/permitted contexts, so a programmatic read
    // often can't work. Try it when we can, but always fall back to focusing
    // the field so a manual Ctrl/Cmd+V (handled by onPaste) still works.
    try {
      if (navigator.clipboard?.readText) {
        const text = (await navigator.clipboard.readText()).trim();
        if (text) {
          setQuery(text);
          setOpen(true);
          goToIdentifier(text);
          return;
        }
      }
    } catch {
      // Denied or unsupported — drop through to the manual-paste fallback.
    }
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (directId) {
        navigate(`/${directId.npub}`);
      } else if (results[highlight]) {
        go(results[highlight].pubkey);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showDropdown = open && query.trim().length >= 1 && (results.length > 0 || isFetching || !!directId);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            if (goToIdentifier(text)) e.preventDefault();
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search by name or paste an npub…"
          aria-label="Search Nostr profiles"
          autoComplete="off"
          className="h-14 w-full rounded-2xl border border-white/15 bg-white/5 pl-5 pr-28 text-base text-white outline-none backdrop-blur-sm transition-colors placeholder:text-slate-500 focus:border-violet-500/60 focus:ring-2 focus:ring-violet-500/40"
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1.5">
          {isFetching && (
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          )}
          <button
            type="button"
            onClick={handlePaste}
            aria-label="Paste from clipboard"
            className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/40"
          >
            <ClipboardPaste className="h-4 w-4" />
            Paste
          </button>
        </div>
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 bottom-full z-30 mb-2 max-h-80 overflow-y-auto rounded-2xl border border-white/10 bg-slate-900/95 p-1.5 shadow-2xl backdrop-blur-md sm:bottom-auto sm:top-full sm:mb-0 sm:mt-2">
          {directId && (
            <button
              type="button"
              onClick={() => navigate(`/${directId.npub}`)}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-white/10"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-500/20 text-violet-300">
                <Search className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-white">Open this identity</span>
                <span className="block truncate text-xs text-slate-400">{directId.npub}</span>
              </span>
            </button>
          )}

          {results.map((r, i) => (
            <ResultRow
              key={r.pubkey}
              result={r}
              active={i === highlight}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => go(r.pubkey)}
            />
          ))}

          {!directId && !isFetching && results.length === 0 && (
            <p className="px-3 py-4 text-center text-sm text-slate-500">No profiles found.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ResultRow({
  result,
  active,
  onClick,
  onMouseEnter,
}: {
  result: RankedProfileResult;
  active: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}) {
  const { metadata, pubkey, relation } = result;
  const name = metadata.display_name || metadata.name || 'Anonymous';
  const picture = sanitizeImageUrl(metadata.picture);
  const nip05 = metadata.nip05?.replace(/^_@/, '');
  const initials = name.slice(0, 2).toUpperCase();

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
        active ? 'bg-white/10' : 'hover:bg-white/5',
      )}
    >
      <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-full bg-white/10">
        {picture ? (
          <img src={picture} alt={name} className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs font-bold text-violet-300">
            {initials}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-white">{name}</span>
          <RelationBadge relation={relation} />
        </span>
        <span className="block truncate text-xs text-slate-400">
          {nip05 || nip19.npubEncode(pubkey).slice(0, 20) + '…'}
        </span>
      </div>
    </button>
  );
}

function RelationBadge({ relation }: { relation: RankedProfileResult['relation'] }) {
  if (relation === 'self') {
    return (
      <span className="shrink-0 rounded-full bg-violet-500/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-300">
        You
      </span>
    );
  }
  if (relation === 'following') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-300">
        <UserCheck className="h-2.5 w-2.5" />
        Following
      </span>
    );
  }
  return null;
}
